/**
 * Extract a human-readable error message from an unknown error object.
 * Works with Nuxt's $fetch FetchError (which has `.data`), standard Error,
 * and falls back to a generic message.
 */
export function extractErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object" && "message" in data) {
      return String((data as { message: unknown }).message);
    }
  }
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
}

/**
 * Extract the HTTP status from an unknown error, covering Nuxt's $fetch
 * FetchError (`statusCode`), fetch Response-shaped errors (`response.status`)
 * and plain `{ status }` objects. Returns undefined when none is present.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as { status?: number; statusCode?: number; response?: { status?: number } };
  return e.status ?? e.statusCode ?? e.response?.status;
}

// Raw strings the backend leaks that should never be shown verbatim to a customer.
const RAW_SERVER_MESSAGES = new Set([
  "Internal Server Error",
  "An unexpected error occurred",
  "Bad Request",
  "Not Found",
  "Forbidden",
  "Unauthorized",
]);

/**
 * Turn an unknown error into a copy-ready string for end users.
 * - Suppresses raw HTTP status names ("Internal Server Error" etc.)
 * - Uses the backend message when it's user-friendly
 * - Falls back to a status-appropriate default
 *
 * Pass `contextFallback` for a context-specific message (e.g. "We couldn't
 * send your reset link. Please try again in a moment.").
 */
export function formatHttpError(error: unknown, contextFallback?: string): string {
  const status = getErrorStatus(error);
  const raw = extractErrorMessage(error);
  const cleaned = RAW_SERVER_MESSAGES.has(raw) ? "" : raw;

  if (status && status >= 500) {
    return contextFallback ?? "We're having trouble with this right now. Please try again in a moment.";
  }
  if (status === 429) {
    return cleaned || "You're doing that too often — please wait and try again.";
  }
  if (status === 401) {
    return cleaned || "Your session has expired. Please log in again.";
  }
  if (status === 403) {
    return cleaned || "You don't have permission to do that.";
  }
  if (status === 404) {
    return cleaned || contextFallback || "We couldn't find what you're looking for.";
  }
  if (status === 400 && cleaned) {
    return cleaned;
  }
  if (cleaned) return cleaned;
  return contextFallback ?? "Something went wrong. Please try again.";
}

/**
 * Check whether a fetch error indicates an "Invalid Token" and, if so,
 * log the user out via the auth store.
 */
export function handleInvalidToken(error: unknown): void {
  const message = extractErrorMessage(error);
  if (message === "Invalid Token") {
    const authStore = useAuthStore();
    authStore.logout();
  }
}

/**
 * Thrown by a store action that CANNOT ISSUE ITS REQUEST because there is no customer token.
 *
 * Several premium actions guard with `if (!hasToken()) return`, which resolves `undefined` and
 * is indistinguishable from success to an `await` that ignores the return value. That is
 * harmless for a read and dangerous for a step in a multi-step submit: the curate screen's swap
 * arm read a skipped `cancelPremiumOrder` as "the old order is gone", cleared the launch pair,
 * and left the customer able to create a second live order for the same date while the original
 * was never cancelled (Darko, PR #187).
 *
 * Shaped to satisfy BOTH extraction paths in this file and the hand-rolled ones on the premium
 * pages: `statusCode` so `getErrorStatus`/`formatHttpError` treat it as the 401 it is, and
 * `data` so a `catch` that reads `err.data` (change-maker.vue does) gets the message rather
 * than falling through to a generic "please try again".
 */
export class MissingAuthTokenError extends Error {
  readonly statusCode = 401;
  readonly data: string;

  constructor(message = "Your session has expired. Please sign in again.") {
    super(message);
    this.name = "MissingAuthTokenError";
    this.data = message;
  }
}
